import { Container } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import React, { FunctionComponent, useState, ReactNode, useEffect, ReactElement } from 'react'

import {
  useCheckoutState,
  useCountry,
  useDisplayProcessingModal,
  useIsAfterpayAppGiftCardFlow,
  useShowLoaderOnly,
  useAPFooter,
  useIsArcadeLogin,
} from '../../state'
import { AppContainer, Footer, Header, PageTransition, OrderComplete, ProcessingModal } from '../../component'
import { CheckoutStatus, Features } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import { HeaderType } from '../types'
import { useRouteBackLink } from '../../config/router'
import styles from './style.module.css'
import clsx from 'clsx'
import { MaintenanceModeContent } from '../../component/MaintenanceModeContent/MaintenanceModeContent'
import { isNullOrUndefined } from '@afterpay/utils'
import { WhiteLabelLayout } from '../WhiteLabelLayout'

interface Props {
  headerType?: HeaderType
  allowClose?: boolean
  hasMargin?: boolean
  children?: ReactNode
  allowFooter?: boolean
  allowPostCheckout?: boolean
  hasDefaultProcessingTimeout?: boolean
}

export const MainLayout: FunctionComponent<Props> = ({
  children,
  headerType = HeaderType.Standard,
  allowClose = true,
  hasMargin = true,
  allowFooter = true,
  allowPostCheckout = true,
  hasDefaultProcessingTimeout = false,
}) => {
  const country = useCountry()
  const { asPath } = useRouter()
  const { status } = useCheckoutState()
  const [initialStatus] = useState(status)
  const isClosed = initialStatus === CheckoutStatus.Closed
  const backLink = useRouteBackLink()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const showLoaderOnly = useShowLoaderOnly()
  const shouldUseAPFooter = useAPFooter()
  const isArcadeLogin = useIsArcadeLogin()

  // TODO: deja vu - figure out page history to determine page transition direction.
  // const direction = PageTransitionDirection.Forward
  const finalAllowClose = allowClose && status !== CheckoutStatus.Confirming && status !== CheckoutStatus.Confirmed && !isAfterpayAppGiftCardFlow

  const isMaintenanceMode = useFlag(Features.MAINTENANCE_MODE)

  const isConfirmChangeToClose = !isClosed && status === CheckoutStatus.Closed
  const isConfirmed = status === CheckoutStatus.Confirmed || isConfirmChangeToClose
  const displayProcessingModal = useDisplayProcessingModal()

  const containerStyles = clsx(!showLoaderOnly && styles.container)
  const showFooter = allowFooter && !showLoaderOnly && shouldUseAPFooter
  const shouldRenderFooter = !showLoaderOnly && !isArcadeLogin
  const [isGiftCardFlow, setIsGiftCardFlow] = useState<boolean | undefined>(undefined)

  // persist gift card flow on processing modal
  useEffect(() => {
    if (isNullOrUndefined(isGiftCardFlow) || isAfterpayAppGiftCardFlow) {
      setIsGiftCardFlow(isAfterpayAppGiftCardFlow)
    }
  }, [isAfterpayAppGiftCardFlow])

  return (
    <Container>
      <AppContainer hasMargin={hasMargin} hasMobileTopMargin={!showLoaderOnly} lockScroll={isConfirmed}>
        {displayProcessingModal && !isClosed && (
          <ProcessingModal allowPostCheckout={allowPostCheckout} hasDefaultTimeout={hasDefaultProcessingTimeout} isAPGiftCardFlow={isGiftCardFlow} />
        )}
        <div className={containerStyles}>
          {!showLoaderOnly && (
            <Header
              country={country}
              allowClose={finalAllowClose}
              type={headerType}
              {...(headerType === HeaderType.Collapsed && {
                backLink,
              })}
            />
          )}
          {!isClosed && !isMaintenanceMode && (
            <PageTransition location={asPath} removeTransition={showLoaderOnly}>
              {children}
            </PageTransition>
          )}
          {isClosed && !isMaintenanceMode && <OrderComplete />}
          {isMaintenanceMode && <MaintenanceModeContent />}
        </div>
        <Footer visible={showFooter} shouldRender={shouldRenderFooter} />
      </AppContainer>
    </Container>
  )
}

export const getLayout = (props?: Props) => (page: ReactElement) => {
  const flowType = page?.props?.flowName

  if (flowType === 'apple-paylater') {
    return <WhiteLabelLayout {...props}>{page}</WhiteLabelLayout>
  }

  return <MainLayout {...props}>{page}</MainLayout>
}
